import { HeartHandshake, PenLine, CalendarHeart, ScrollText, ArrowUpRight, Users, FlaskConical, Compass, Lightbulb, ShieldCheck, Mail } from "lucide-react";
import { Reveal, Counter } from "../lib/motion";

const OFFERINGS = [
  {
    icon: PenLine,
    title: "Grant Writing & Acquisition",
    desc: "Compelling, data-driven grant proposals engineered to secure funding from corporate, federal, and private foundations.",
    tag: "FUNDING",
  },
  {
    icon: CalendarHeart,
    title: "End-to-End Event Management",
    desc: "Organizing, planning, and executing charity galas, community fundraisers, and high-impact stakeholder conferences.",
    tag: "EVENTS",
  },
  {
    icon: ScrollText,
    title: "Strategic Fundraising Compliance",
    desc: "Aligning events and grant tracking with nonprofit compliance standards to ensure long-term donor transparency.",
    tag: "GOVERNANCE",
  },
];

const STATS = [
  { to: 12, prefix: "$", suffix: "M+", label: "Funds & grants secured" },
  { to: 150, suffix: "+", label: "Events delivered" },
  { to: 100, suffix: "%", label: "Donor transparency compliance" },
];

const NONPROFIT_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_nexis-future/artifacts/7435f0da586f4e17_NEXIS%20Mental%20Health%20Emblem.png";

const GRANT_FRAMEWORK = [
  {
    step: "01",
    title: "Discovery & Funder Alignment",
    desc: "Map your mission to the right funders — eligibility screening, funder research, and a prioritized pipeline of corporate, federal, and foundation opportunities.",
  },
  {
    step: "02",
    title: "Needs Statement & Evidence",
    desc: "A data-driven case for support: community data, measurable gaps, and the human story that makes reviewers lean in.",
  },
  {
    step: "03",
    title: "Program Design & Logic Model",
    desc: "SMART objectives, activities, and outcomes tied into a defensible logic model funders can audit at a glance.",
  },
  {
    step: "04",
    title: "Budget & Compliance",
    desc: "Line-item budgets with full justification, cost-allocation discipline, and alignment to each funder's compliance requirements.",
  },
  {
    step: "05",
    title: "Submission, Reporting & Stewardship",
    desc: "Red-team review, on-time submission, then post-award reporting and donor stewardship that keeps the next grant warmer than the last.",
  },
];

const PILLARS = [
  { icon: Users, title: "People", desc: "Protecting dignity, compassion, and human connection." },
  { icon: FlaskConical, title: "Research", desc: "Advancing evidence-informed understanding and collaboration." },
  { icon: HeartHandshake, title: "Support", desc: "Strengthening families, caregivers, and communities." },
  { icon: Compass, title: "Access", desc: "Helping people discover appropriate resources and services." },
  { icon: Lightbulb, title: "Innovation", desc: "Exploring responsible technology that supports human well-being." },
];

const CREDO = [
  {
    label: "VISION",
    text: "A world where every person facing a mental health challenge is met with dignity, evidence, and community — a healthier tomorrow for a brighter humanity.",
  },
  {
    label: "MISSION",
    text: "To strengthen the people and organizations who care for mental health — through research collaboration, community support, resource navigation, and responsibly governed technology.",
  },
  {
    label: "FOUNDATIONAL PHILOSOPHY",
    text: "Technology must serve human dignity, never substitute for it. Every tool we explore is governed, auditable, and subordinate to human judgment, professional expertise, and lived experience.",
  },
];

export default function Nonprofit() {
  return (
    <section
      id="nonprofit"
      className="relative border-t border-white/5 bg-nexis-bg/80 py-28 backdrop-blur-sm md:py-40"
      data-testid="nonprofit-section"
    >
      <div className="orb orb-purple left-[3%] top-[15%] h-[340px] w-[340px]" />
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-nexis-purple">
              <HeartHandshake className="h-4 w-4" /> — SPECIALIZED NONPROFIT SERVICES
            </span>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-light tracking-tight lg:text-5xl">
              A dedicated wing for <span className="text-gradient">mission-driven organizations</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-white/50">
              Beyond enterprise tech and infrastructure, we operate a specialized practice tailored to the nonprofit sector — from funding to flawless execution.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {OFFERINGS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div
                className="glass glass-hover group relative h-full overflow-hidden rounded-3xl p-8"
                data-testid={`nonprofit-card-${i}`}
              >
                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-nexis-purple/20 to-transparent opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-nexis-purple ring-1 ring-white/10">
                    <o.icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-white/30">{o.tag}</span>
                </div>
                <h3 className="mt-8 font-display text-xl font-medium tracking-tight">{o.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{o.desc}</p>
                <div className="mt-6 flex items-center gap-1.5 text-xs text-white/30 transition-colors duration-300 group-hover:text-nexis-purple">
                  Partner with us <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="glass rounded-2xl px-7 py-6 text-center" data-testid={`nonprofit-stat-${i}`}>
                <div className="font-display text-4xl font-light tracking-tight text-white">
                  <Counter to={s.to} prefix={s.prefix || ""} suffix={s.suffix || ""} />
                </div>
                <div className="mt-2 font-mono text-[11px] tracking-widest text-white/40">
                  {s.label.toUpperCase()}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ——— NEXIS Mental Health Initiative ——— */}
        <div className="mt-24" data-testid="mhi-section">
          <div className="grid items-center gap-10 lg:grid-cols-[1.25fr_1fr]">
            <div>
              <Reveal>
                <span className="font-mono text-xs tracking-[0.25em] text-emerald-300">— NEXIS MENTAL HEALTH INITIATIVE</span>
                <h3 className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight lg:text-5xl" data-testid="mhi-headline">
                  Human dignity through <span className="text-gradient">governed intelligence</span>.
                </h3>
              </Reveal>
              <div className="mt-8 space-y-3">
                {CREDO.map((c, i) => (
                  <Reveal key={c.label} delay={i * 0.07}>
                    <div className="glass rounded-2xl p-5" data-testid={`mhi-credo-${i}`}>
                      <span className="font-mono text-[10px] tracking-[0.25em] text-cyan-300">{c.label}</span>
                      <p className="mt-2 text-sm leading-relaxed text-white/65">{c.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden rounded-[2rem] glass" data-testid="nonprofit-image">
                <img
                  src={NONPROFIT_IMG}
                  alt="NEXIS Mental Health Initiative — people, research, support, access, innovation"
                  loading="lazy"
                  className="w-full object-contain"
                />
                <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between font-mono text-[10px] tracking-widest text-white/50">
                  <span>NEXIS MENTAL HEALTH INITIATIVE</span>
                  <span className="text-emerald-300">● MISSION-DRIVEN</span>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Five pillars */}
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="mhi-pillars">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="glass glass-hover flex h-full flex-col rounded-2xl p-5" data-testid={`mhi-pillar-${i}`}>
                  <p.icon className="h-5 w-5 text-emerald-300" />
                  <h4 className="mt-4 font-display text-base font-medium text-white">{p.title}</h4>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Grant writing framework — proposed organizational capabilities */}
        <div className="mt-20" data-testid="grant-framework">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] text-nexis-purple">— THE NEXIS GRANT WRITING FRAMEWORK</span>
            <h3 className="mt-4 max-w-xl font-display text-3xl font-light tracking-tight lg:text-4xl">
              From mission to <span className="text-gradient">funded program</span>, in five governed steps.
            </h3>
            <p className="mt-3 max-w-2xl text-xs leading-relaxed text-white/40">
              These represent proposed organizational capabilities under development — not evidence of established funding, nonprofit status, or operational clinical services.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-3 lg:grid-cols-2">
            {GRANT_FRAMEWORK.map((g, i) => (
              <Reveal key={g.step} delay={i * 0.06}>
                <div className="glass glass-hover flex h-full gap-5 rounded-2xl p-5" data-testid={`grant-step-${i}`}>
                  <span className="font-mono text-sm tracking-widest text-nexis-purple">{g.step}</span>
                  <div>
                    <h4 className="font-display text-base font-medium text-white">{g.title}</h4>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/55">{g.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Status, responsible AI boundaries, partnership */}
        <div className="mt-16 grid gap-4 lg:grid-cols-2">
          <Reveal>
            <div className="glass h-full rounded-2xl p-6" data-testid="mhi-status">
              <span className="font-mono text-[10px] tracking-[0.25em] text-white/40">ORGANIZATIONAL STATUS</span>
              <p className="mt-3 text-sm leading-relaxed text-white/65">
                The NEXIS Mental Health Initiative is an emerging humanitarian initiative in development. Formal nonprofit status has not yet been established.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="glass h-full rounded-2xl p-6" data-testid="mhi-ai-boundaries">
              <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-cyan-300">
                <ShieldCheck className="h-3.5 w-3.5" /> RESPONSIBLE AI BOUNDARIES
              </span>
              <p className="mt-3 text-sm leading-relaxed text-white/65">
                NEXIS technology is intended to support human decision-making and organizational coordination. It does not replace licensed mental health professionals, diagnose conditions, or independently determine treatment.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 overflow-hidden rounded-[2rem] glass p-8 text-center md:p-12" data-testid="mhi-partnership">
            <p className="mx-auto max-w-2xl font-display text-2xl font-light leading-snug tracking-tight lg:text-3xl">
              "Join us in building a future where <span className="text-gradient">compassion, responsible innovation, and community support</span> converge."
            </p>
            <a
              href="mailto:hello@nexis.team?subject=Partnership%20interest%20%E2%80%94%20NEXIS%20Mental%20Health%20Initiative"
              className="mt-8 inline-flex items-center gap-2 rounded-full glass glass-hover px-7 py-3.5 text-sm font-medium text-white"
              data-testid="mhi-partner-btn"
            >
              <Mail className="h-4 w-4 text-emerald-300" /> Express partnership interest
            </a>
            <p className="mt-4 text-[11px] text-white/35">
              Nonclinical partnership inquiries only — please do not include personal health information or mental health histories.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
